import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Users, Plus, Search, MoreVertical, Edit, Key, Shield, 
  UserX, UserCheck, RefreshCw, Mail, Send, Clock, CheckCircle, 
  XCircle, AlertCircle, Trash2, DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

export default function UserManagement() {
  const { api } = useAuth();
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('users');
  
  // Dialog states
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Invite form state
  const [inviteData, setInviteData] = useState({
    email: '',
    role: 'employee',
    first_name: '',
    last_name: '',
    phone_number: ''
  });

  // Edit form state
  const [editData, setEditData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    alternate_email: '',
    phone_number: '',
    // Payroll settings (for employees only)
    payroll_hourly_rate: 0,
    payroll_overtime_rate: 0,
    payroll_active: true,
    employee_number: '',
    vacation_pay_percent: 4.0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, invitationsRes, pendingRes] = await Promise.all([
        api.get('/users'),
        api.get('/invitations'),
        api.get('/users/pending')
      ]);
      setUsers(usersRes.data);
      setInvitations(invitationsRes.data);
      setPendingUsers(pendingRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      await api.post('/invitations', inviteData);
      toast.success(`Invitation sent to ${inviteData.email}`);
      setInviteDialogOpen(false);
      setInviteData({ email: '', role: 'employee', first_name: '', last_name: '', phone_number: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setFormLoading(false);
    }
  };

  const handleResendInvitation = async (invitation) => {
    try {
      await api.post(`/invitations/${invitation.id}/resend`);
      toast.success('Invitation resent successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invitation');
    }
  };

  const handleCancelInvitation = async (invitation) => {
    try {
      await api.delete(`/invitations/${invitation.id}`);
      toast.success('Invitation cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel invitation');
    }
  };

  const handleApproveUser = async (user) => {
    try {
      await api.post(`/users/${user.id}/approve`);
      toast.success(`${user.first_name} ${user.last_name} approved successfully`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    }
  };

  const handleRejectUser = async () => {
    if (!selectedUser) return;
    setFormLoading(true);

    try {
      await api.post(`/users/${selectedUser.id}/reject?reason=${encodeURIComponent(rejectionReason)}`);
      toast.success(`User rejected`);
      setRejectDialogOpen(false);
      setRejectionReason('');
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      await api.patch(`/users/${selectedUser.id}`, editData);
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeactivateUser = async (user) => {
    try {
      await api.post(`/users/${user.id}/deactivate`);
      toast.success('User deactivated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate user');
    }
  };

  const handleActivateUser = async (user) => {
    try {
      await api.post(`/users/${user.id}/activate`);
      toast.success('User activated');
      fetchData();
    } catch (error) {
      toast.error('Failed to activate user');
    }
  };

  const handleResetPassword = async (user) => {
    try {
      const response = await api.post(`/users/${user.id}/reset-password`);
      toast.success(`Password reset. Temp password: ${response.data.temp_password}`);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleResetMFA = async (user) => {
    try {
      await api.post(`/users/${user.id}/reset-mfa`);
      toast.success('MFA reset successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to reset MFA');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditData({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      alternate_email: user.alternate_email || '',
      phone_number: user.phone_number || '',
      // Payroll settings (loaded from user record)
      payroll_hourly_rate: user.payroll_hourly_rate || 0,
      payroll_overtime_rate: user.payroll_overtime_rate || 0,
      payroll_active: user.payroll_active !== false,
      employee_number: user.employee_number || '',
      vacation_pay_percent: user.vacation_pay_percent || 4.0
    });
    setEditDialogOpen(true);
  };

  const openRejectDialog = (user) => {
    setSelectedUser(user);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.last_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="h-3 w-3 mr-1" />Active</Badge>;
      case 'pending_approval':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case 'invited':
        return <Badge className="bg-blue-100 text-blue-700"><Mail className="h-3 w-3 mr-1" />Invited</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      case 'deactivated':
        return <Badge className="bg-slate-100 text-slate-700"><UserX className="h-3 w-3 mr-1" />Deactivated</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-100 text-purple-700';
      case 'employee': return 'bg-blue-100 text-blue-700';
      case 'physician': return 'bg-teal-100 text-teal-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">User Management</h1>
          <p className="text-slate-500 mt-1">Manage staff and physician accounts</p>
        </div>
        <Button 
          className="bg-teal-600 hover:bg-teal-700"
          onClick={() => setInviteDialogOpen(true)}
          data-testid="invite-user-btn"
        >
          <Send className="h-4 w-4 mr-2" />
          Invite User
        </Button>
      </div>

      {/* Pending Approvals Alert */}
      {pendingUsers.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">
                  {pendingUsers.length} user{pendingUsers.length > 1 ? 's' : ''} awaiting approval
                </p>
                <p className="text-sm text-amber-600">Review and approve new user registrations</p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={() => setActiveTab('pending')}
              >
                Review Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            All Users ({users.length})
          </TabsTrigger>
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Pending Approval ({pendingUsers.length})
          </TabsTrigger>
          <TabsTrigger value="invitations" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Invitations ({invitations.length})
          </TabsTrigger>
        </TabsList>

        {/* All Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-users-input"
                  />
                </div>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="physician">Physician</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending_approval">Pending</SelectItem>
                    <SelectItem value="deactivated">Deactivated</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <RefreshCw className="h-8 w-8 animate-spin mx-auto text-slate-400" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>MFA</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map((user) => (
                        <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                          <TableCell className="font-medium">
                            {user.first_name} {user.last_name}
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <Badge className={getRoleBadgeColor(user.role)}>
                              {user.role}
                            </Badge>
                          </TableCell>
                          <TableCell>{getStatusBadge(user.status)}</TableCell>
                          <TableCell>
                            {user.mfa_enabled ? (
                              <Badge className="bg-emerald-100 text-emerald-700">
                                <Shield className="h-3 w-3 mr-1" />
                                Enabled
                              </Badge>
                            ) : (
                              <Badge variant="secondary">Disabled</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {formatDate(user.created_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditDialog(user)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit User
                                </DropdownMenuItem>
                                {user.status === 'active' && (
                                  <>
                                    <DropdownMenuItem onClick={() => handleResetPassword(user)}>
                                      <Key className="h-4 w-4 mr-2" />
                                      Reset Password
                                    </DropdownMenuItem>
                                    {user.mfa_enabled && (
                                      <DropdownMenuItem onClick={() => handleResetMFA(user)}>
                                        <Shield className="h-4 w-4 mr-2" />
                                        Reset MFA
                                      </DropdownMenuItem>
                                    )}
                                  </>
                                )}
                                <DropdownMenuSeparator />
                                {user.status === 'active' ? (
                                  <DropdownMenuItem 
                                    onClick={() => handleDeactivateUser(user)}
                                    className="text-red-600"
                                  >
                                    <UserX className="h-4 w-4 mr-2" />
                                    Deactivate
                                  </DropdownMenuItem>
                                ) : user.status === 'deactivated' ? (
                                  <DropdownMenuItem 
                                    onClick={() => handleActivateUser(user)}
                                    className="text-emerald-600"
                                  >
                                    <UserCheck className="h-4 w-4 mr-2" />
                                    Activate
                                  </DropdownMenuItem>
                                ) : null}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Approvals Tab */}
        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>Pending Approvals</CardTitle>
              <CardDescription>Users who have completed their profile and are awaiting approval</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingUsers.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No pending approvals</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingUsers.map((user) => (
                    <Card key={user.id} className="border-slate-200">
                      <CardContent className="py-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                                <span className="text-lg font-medium text-slate-600">
                                  {user.first_name[0]}{user.last_name[0]}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium">{user.first_name} {user.last_name}</p>
                                <p className="text-sm text-slate-500">{user.email}</p>
                              </div>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <Badge className={getRoleBadgeColor(user.role)}>{user.role}</Badge>
                              <Badge variant="outline">
                                Invited: {formatDate(user.invited_at)}
                              </Badge>
                              {user.alternate_email && (
                                <Badge variant="outline" className="bg-slate-50">
                                  MFA: {user.alternate_email}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline"
                              className="border-red-200 text-red-600 hover:bg-red-50"
                              onClick={() => openRejectDialog(user)}
                              data-testid={`reject-user-${user.id}`}
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Reject
                            </Button>
                            <Button 
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => handleApproveUser(user)}
                              data-testid={`approve-user-${user.id}`}
                            >
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Approve
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invitations Tab */}
        <TabsContent value="invitations">
          <Card>
            <CardHeader>
              <CardTitle>Pending Invitations</CardTitle>
              <CardDescription>Invitations that haven't been accepted yet</CardDescription>
            </CardHeader>
            <CardContent>
              {invitations.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Mail className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No pending invitations</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Sent</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invitations.map((inv) => (
                        <TableRow key={inv.id}>
                          <TableCell className="font-medium">{inv.email}</TableCell>
                          <TableCell>
                            {inv.first_name && inv.last_name 
                              ? `${inv.first_name} ${inv.last_name}` 
                              : '-'}
                          </TableCell>
                          <TableCell>
                            <Badge className={getRoleBadgeColor(inv.role)}>{inv.role}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {formatDate(inv.created_at)}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {formatDate(inv.expires_at)}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleResendInvitation(inv)}
                                data-testid={`resend-invite-${inv.id}`}
                              >
                                <RefreshCw className="h-3 w-3 mr-1" />
                                Resend
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleCancelInvitation(inv)}
                                data-testid={`cancel-invite-${inv.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Invite User Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Invite New User</DialogTitle>
            <DialogDescription>
              Send an invitation email to add a new staff member or physician
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInviteUser}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="invite_email">Email Address *</Label>
                <Input
                  id="invite_email"
                  type="email"
                  placeholder="user@example.com"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                  required
                  data-testid="invite-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite_role">Role *</Label>
                <Select 
                  value={inviteData.role} 
                  onValueChange={(value) => setInviteData({ ...inviteData, role: value })}
                >
                  <SelectTrigger data-testid="invite-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="physician">Physician</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="invite_first_name">First Name</Label>
                  <Input
                    id="invite_first_name"
                    placeholder="Optional"
                    value={inviteData.first_name}
                    onChange={(e) => setInviteData({ ...inviteData, first_name: e.target.value })}
                    data-testid="invite-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite_last_name">Last Name</Label>
                  <Input
                    id="invite_last_name"
                    placeholder="Optional"
                    value={inviteData.last_name}
                    onChange={(e) => setInviteData({ ...inviteData, last_name: e.target.value })}
                    data-testid="invite-last-name"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500">
                The invited user will complete their profile and set their password. Their account will then require your approval before activation.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInviteDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-teal-600 hover:bg-teal-700"
                disabled={formLoading}
                data-testid="send-invite-btn"
              >
                {formLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Invitation
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user information
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateUser}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_first_name">First Name</Label>
                  <Input
                    id="edit_first_name"
                    value={editData.first_name}
                    onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
                    required
                    data-testid="edit-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_last_name">Last Name</Label>
                  <Input
                    id="edit_last_name"
                    value={editData.last_name}
                    onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
                    required
                    data-testid="edit-last-name"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_alternate_email">Alternate Email (MFA)</Label>
                <Input
                  id="edit_alternate_email"
                  type="email"
                  value={editData.alternate_email}
                  onChange={(e) => setEditData({ ...editData, alternate_email: e.target.value })}
                  data-testid="edit-alternate-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_phone">Phone Number</Label>
                <Input
                  id="edit_phone"
                  type="tel"
                  value={editData.phone_number}
                  onChange={(e) => setEditData({ ...editData, phone_number: e.target.value })}
                  data-testid="edit-phone"
                />
              </div>
              
              {/* Payroll Settings - Only shown for employees */}
              {selectedUser?.role === 'employee' && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium text-sm text-slate-700 mb-3 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Payroll Settings
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit_hourly_rate">Hourly Rate ($) *</Label>
                      <Input
                        id="edit_hourly_rate"
                        type="number"
                        step="0.01"
                        min="0"
                        value={editData.payroll_hourly_rate}
                        onChange={(e) => setEditData({ ...editData, payroll_hourly_rate: parseFloat(e.target.value) || 0 })}
                        data-testid="edit-hourly-rate"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit_ot_rate">Overtime Rate ($)</Label>
                      <Input
                        id="edit_ot_rate"
                        type="number"
                        step="0.01"
                        min="0"
                        value={editData.payroll_overtime_rate}
                        onChange={(e) => setEditData({ ...editData, payroll_overtime_rate: parseFloat(e.target.value) || 0 })}
                        placeholder="1.5x if empty"
                        data-testid="edit-ot-rate"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div className="space-y-2">
                      <Label htmlFor="edit_emp_num">Employee Number</Label>
                      <Input
                        id="edit_emp_num"
                        value={editData.employee_number}
                        onChange={(e) => setEditData({ ...editData, employee_number: e.target.value })}
                        placeholder="Optional"
                        data-testid="edit-emp-num"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit_vac_pct">Vacation Pay %</Label>
                      <Input
                        id="edit_vac_pct"
                        type="number"
                        step="0.5"
                        min="0"
                        value={editData.vacation_pay_percent}
                        onChange={(e) => setEditData({ ...editData, vacation_pay_percent: parseFloat(e.target.value) || 4 })}
                        data-testid="edit-vac-pct"
                      />
                    </div>
                  </div>
                  {editData.payroll_hourly_rate <= 0 && (
                    <p className="text-xs text-amber-600 mt-2">
                      Note: Hourly rate is required for payroll processing
                    </p>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-teal-600 hover:bg-teal-700"
                disabled={formLoading}
                data-testid="edit-user-submit"
              >
                {formLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Update User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reject User Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Reject User</DialogTitle>
            <DialogDescription>
              {selectedUser && `Reject ${selectedUser.first_name} ${selectedUser.last_name}'s account request`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rejection_reason">Reason (Optional)</Label>
              <Input
                id="rejection_reason"
                placeholder="Provide a reason for rejection"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                data-testid="rejection-reason"
              />
            </div>
            <p className="text-sm text-slate-500">
              The user will be notified via email about this decision.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRejectUser}
              className="bg-red-600 hover:bg-red-700"
              disabled={formLoading}
              data-testid="confirm-reject-btn"
            >
              {formLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Reject User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
